import React from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { UI_OPERATION_STATUS_ERROR } from '@uncinc/react-drupal-core';
import { QuantityPicker } from 'react-qty-picker';
import {
  Button,
  Container,
  FormikWrapper,
} from '@uncinc/uncinc-react-kitchen-sink';
import Select from '@uncinc/uncinc-react-kitchen-sink/dist/components/Form/Select';
import { useTranslation } from 'react-i18next';

import { ROUTE_CHECKOUT_V2, ROUTE_USER_CHECK } from '../../../../config/routes';
import ProductBlock from '../../../../components/Product/ProductBlock';
import useProduct from '../../../../hooks/useProduct';

import '../style.scss';

// TODO: Renders the Product paragraph (field_product). This duplicates the
// product purchase logic in components/Product/Generic (GenericProduct, used by
// field_commerce_product): same useProduct + ProductBlock render. Unify the two
// into one shared component when there is time — kept separate for now to avoid
// risk.
const ParagraphProduct = ({ entity }) => {
  const loggedIn = useSelector((state) => state.getIn(['login', 'loggedIn'], false),
  );
  const { t } = useTranslation();
  const productAddStatus = useSelector((state) => state.getIn(['ui', 'commerce', 'operations', 'cart_product_add', 'status']),
  );

  // Per-order max from the initial variation: the backend normalizer exposes
  // the CMS cap under `maximum_quantity` (a string); 500 acts as "no limit"
  // when it isn't set.
  const variationForMax = entity.getIn([
    'field_product',
    0,
    'entity',
    'variations',
    0,
  ]);
  const maximumQuantity =
    Number(variationForMax?.get('maximum_quantity')) || 500;

  const {
    quantity,
    setQuantity,
    handleOrderClick,
    handleVariationChange,
    isLoading,
    title,
    variationPrice,
    hasMultipleVariations,
    body,
    isDigitalProduct,
    isAvailable,
    discountTiers,
    productImage,
  } = useProduct(entity, {
    entityPath: ['field_product', 0, 'entity'],
    maxQuantity: maximumQuantity,
    trackViewItem: true,
  });

  const isFree = Number.parseFloat(variationPrice) === 0;
  // A free e-learning is a registration ("Aanmelden"); a free physical product
  // is still ordered and shipped, so it keeps the normal "Bestellen" button.
  const isFreeRegistration = isFree && isDigitalProduct;

  const buttonIconClasses = classNames('icon icon-variant-2', {
    'icon-right-arrow': isFreeRegistration,
    'icon-shopping-cart': !isFreeRegistration,
  });

  const getButtonHref = () => {
    if (!isDigitalProduct) return '#';
    return loggedIn ? ROUTE_CHECKOUT_V2 : ROUTE_USER_CHECK;
  };

  const variations = entity.getIn(['field_product', 0, 'entity', 'variations']);
  const variationOptions =
    variations
      ?.map((_, index) => ({
        value: index,
        label: t('cart.variant_option', { number: index + 1 }),
      }))
      .toArray() || [];

  const variationSelect = hasMultipleVariations ? (
    <FormikWrapper>
      <Select
        required
        placeholder={t('cart.choose_variant')}
        fieldName="product_variation"
        options={variationOptions}
        onChange={handleVariationChange}
      />
    </FormikWrapper>
  ) : null;

  const orderButton = isAvailable ? (
    <Button
      href={getButtonHref()}
      className="Button--primary Button--order"
      onClick={handleOrderClick}
      disabled={isLoading}
    >
      {isFreeRegistration ? t('cart.register') : t('cart.checkout')}
      <div className={buttonIconClasses} />
    </Button>
  ) : (
    <Button to="#" className="Button--primary Button--order" disabled>
      {t('cart.temporarily_unavailable')}
    </Button>
  );

  const errorMessage = productAddStatus === UI_OPERATION_STATUS_ERROR && (
    <div className="Product__product__error Form__error">
      {t('cart.maximum_quantity_limit_reached', { maximumQuantity })}
    </div>
  );

  // Both paid and free products use the same product card. Free products show
  // "Gratis" and omit the VAT note (driven by the isFree prop); unavailable
  // products render the card with a disabled order button.
  return (
    <Container className="Paragraph Product ParagraphProduct ParagraphCTA--variant--get-started">
      <div className="row">
        <div className="col-12 col-lg-8 offset-lg-2">
          <ProductBlock
            productImage={productImage}
            title={title}
            description={body}
            price={variationPrice}
            isFree={isFree}
            discountTiers={discountTiers}
            quantity={quantity}
            variationSelect={variationSelect}
            orderBar={
              <>
                {!isFreeRegistration && (
                  <QuantityPicker
                    min={1}
                    max={maximumQuantity}
                    value={quantity}
                    onChange={setQuantity}
                    width="2.9rem"
                  />
                )}
                {orderButton}
              </>
            }
          />
          {errorMessage}
        </div>
      </div>
    </Container>
  );
};

export default ParagraphProduct;
